import type { TMatterRetriever } from "../../services/matter-retriever";
import type { TMatterAssigner } from "../../services/assign-to-matter-service";
import type { TMatter } from "../../models/matter-model";
import type { Props as MatterPresentationalProps } from "../Matter/MatterPresentational";
import { useEffect, useState } from "react";
import { TaskpanePresentational } from "./TaskpanePresentational";
import { AttachmentsContainer } from "../Attachments/AttachmentsContainer";
import { SearchContainer } from "../Search/SearchContainer";
import { useNotificationStore } from "../Notifications/notification-store";
import { useItemStore } from "../../mailitem/mail-item-store";
import { LogoutContainer } from "../Logout/LogoutContainer";
import { SmokeballAuthenticator, TSmokeballTier } from "../../services/smokeball-authenticator";
import { useHistory } from "react-router-dom";
import { ErrorType } from "../../helpers/ErrorType";
import { MetricPayload, UNKNOWN_METRICS_VALUE } from "../../models/metric-model";
import { createAuthorisedApiRequest } from "../../helpers/create-authorised-request";
import { fetchWrapper } from "../../helpers/fetch-wrapper";
import { getDiagnostics } from "../../helpers/DiagnosticsHelper";
import { ViewType } from "../Search/SearchPresentational";

export type Props = {
  tier: TSmokeballTier;
  assignedMatter: TMatter | null;
  matterAssigner: TMatterAssigner;
  matterRetriever: TMatterRetriever;
  isCheckingForAssignedMatter: boolean;
  authenticator: SmokeballAuthenticator;

  setAssignedMatter: (m: TMatter | null) => void;
  openSelectFolder?: (matterId: string, cb: (folderId: string, display: string) => void) => void;
};

export const TaskpaneReadContainer = (props: Props) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const [saveToFolderId, setSaveToFolderId] = useState<string>("");
  const [saveToFolderDisplay, setSaveToFolderdisplay] = useState<string>("");
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);
  const [selectedMatter, setSelectedMatter] = useState<TMatter | null>(props.assignedMatter);

  const history = useHistory();
  const itemUpdateTs = useItemStore((s) => s.updateTs);
  const mailItem = useItemStore((s) => s.wrappedMailItem);

  const errorNotification = useNotificationStore((s) => s.errorNotification);

  useEffect(() => {
    // so we need to reset some things when the mailItem changes
    setSelectedMatter(props.assignedMatter);
    setSelectedAttachmentIds([]);
    setSaveToFolderId("");
    setSaveToFolderdisplay("");
    setIsAssigning(false);
  }, [itemUpdateTs, props.assignedMatter?.matterId]);

  const onMatterSelected = async (args: Parameters<NonNullable<MatterPresentationalProps["onMatterSelected"]>>[0]) => {
    const matter = args.isSelected ? args.matter : null;
    setSelectedMatter(matter);

    if (matter === null && props.assignedMatter) {
      return assignMatter(null, args.searchViewType);
    }
    if (matter !== null && mailItem) {
      const attachments = await mailItem.getAttachments();
      if (!attachments || attachments.length === 0) {
        await assignMatter(matter, args.searchViewType);
      }
    }
  };

  const assignClicked = async (searchViewType: MatterPresentationalProps["searchViewType"]) => {
    if (selectedAttachmentIds.length > 0) {
      const metricName = "outlook_addin_attachment_saved";

      try {
        const diagnostics = getDiagnostics();

        // Metric for when a user clicks "save to matter" and the mail has a minimum of 1 attachment selected
        const metricPayload: MetricPayload = {
          Metric: {
            Name: metricName,
            Timestamp: new Date().toISOString(), // Current UTC time
            Dimensions: {
              Platform: diagnostics?.platform || UNKNOWN_METRICS_VALUE,
              Version: diagnostics?.version || UNKNOWN_METRICS_VALUE,
              HostName: diagnostics?.hostName || UNKNOWN_METRICS_VALUE,
              HostVersion: diagnostics?.hostVersion || UNKNOWN_METRICS_VALUE,
              Product: "",
            },
          },
        };

        const { url, requestInit } = await createAuthorisedApiRequest("AddMetricUrl", metricPayload);
        const result = await fetchWrapper(url, requestInit);

        if (result.err) {
          console.error(`Add metric call returned an error when saving ${metricName} metric: `, result.err);
        }
      } catch (error) {
        console.error(`Error occurred when saving ${metricName} metric: `, error);
      }
    }

    return assignMatter(selectedMatter, searchViewType);
  };

  const assignMatter = async (matter: TMatter | null, searchViewType: ViewType) => {
    if (mailItem) {
      const matterId = matter?.matterId ?? null;
      const actionName: "assigning" | "unassigning" = matterId ? "assigning" : "unassigning";
      setIsAssigning(true);

      try {
        let assignResult;

        if (matterId) {
          const allAttachments = await mailItem.getAttachments();
          const attachments = allAttachments?.filter((f) => selectedAttachmentIds.includes(f.id)) ?? [];

          assignResult = await props.matterAssigner.assignEmailToMatter(
            mailItem,
            matterId,
            matterId ? attachments : [],
            saveToFolderId,
            searchViewType
          );
        } else {
          assignResult = await props.matterAssigner.unAssignEmailFromMatter(
            mailItem,
            props.assignedMatter?.matterId || ""
          );
        }

        if (assignResult.result) {
          props.setAssignedMatter(matter);
          setSelectedAttachmentIds([]);
        } else {
          if (assignResult.err === ErrorType.Unauthorized) {
            // The call to assign/unassign was unauthorized so we've logged out, so show the login page
            errorNotification("Your authorization has expired. Please sign in again");
            history.push("/start");
            location.reload();
          } else if (assignResult.err === ErrorType.NoMatterPermission) {
            errorNotification("You currently don't have permission to access this Matter");
          } else {
            const error = assignResult.err || "Unknown error";
            console.error("Error Assigning Draft", error);
            errorNotification(`Error ${actionName} email to matter`, error);
          }
        }
      } catch (err) {
        errorNotification(`Error ${actionName} email to matter`, err);
      } finally {
        setIsAssigning(false);
      }
    }
  };

  const saveToContent = (
    <div>
      Save to Folder{" "}
      <span
        className="font-semibold text-primary-blue-560 hover:underline hover:cursor-pointer"
        onClick={() => {
          selectedMatter &&
            props.openSelectFolder &&
            props.openSelectFolder(selectedMatter.matterId, (folderId: string, display: string) => {
              setSaveToFolderId(folderId);
              setSaveToFolderdisplay(display);
            });
        }}
      >
        {saveToFolderDisplay || "Documents"}
      </span>{" "}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 max-h-full min-h-0">
        <TaskpanePresentational
          tier={props.tier}
          isAssigning={isAssigning}
          assignMatter={assignClicked}
          onMatterSelected={onMatterSelected}
          selectedMatter={selectedMatter || undefined}
          assignedMatter={props.assignedMatter || undefined}
          isCheckingForAssignedMatter={props.isCheckingForAssignedMatter}
          searchContent={
            selectedMatter || props.assignedMatter ? null : (
              <SearchContainer
                tier={props.tier}
                onMatterSelected={onMatterSelected}
                matterRetriever={props.matterRetriever}
              />
            )
          }
          attachmentsContent={
            props.assignedMatter || !selectedMatter ? null : (
              <AttachmentsContainer
                selected={selectedAttachmentIds}
                setSelected={setSelectedAttachmentIds}
                saveToContent={saveToContent}
              />
            )
          }
        />
      </div>
      <div className="flex flex-col justify-end mt-auto">
        <LogoutContainer authenticator={props.authenticator} />
      </div>
    </div>
  );
};
